import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { fetchMenu, fetchOrders } from "../services/apiService";
import { getConfig } from "../config";

const DataContext = createContext();

export const DataProvider = ({ children }) => {
  const { guestAuthEnabled } = getConfig();
  const [menu, setMenu] = useState([]);
  const [existingOrders, setExistingOrders] = useState([]);
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const fetchMenuData = useCallback(async () => {
    try {
      const menuResponse = await fetchMenu();
      const activeItems = (menuResponse.items || []).filter(
        (item) => item && item.status === "active" && item.isAvailable !== false
      );
      setMenu(activeItems);
      return {
        items: activeItems,
        qrContext: menuResponse.qrContext ?? null,
      };
    } catch (error) {
      console.error("[DataProvider] Error fetching menu:", error.message);
      throw error;
    }
  }, []);

  const clearExistingOrders = useCallback(() => {
    setExistingOrders([]);
  }, []);

  const fetchExistingOrdersData = useCallback(async () => {
    if (guestAuthEnabled && !isAuthenticated) {
      clearExistingOrders();
      return [];
    }

    try {
      const token = guestAuthEnabled ? await getAccessTokenSilently() : null;
      const ordersResponse = await fetchOrders(token);
      const orders = ordersResponse.orders || [];
      setExistingOrders(orders);
      return orders;
    } catch (error) {
      console.error("[DataProvider] Error fetching orders:", error.message);
      throw error;
    }
  }, [clearExistingOrders, guestAuthEnabled, getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    if (guestAuthEnabled && !isAuthenticated) {
      clearExistingOrders();
    }
  }, [clearExistingOrders, guestAuthEnabled, isAuthenticated]);

  return (
    <DataContext.Provider
      value={{
        menu,
        existingOrders,
        fetchMenuData,
        fetchExistingOrdersData,
        clearExistingOrders,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => useContext(DataContext);
